<template>
  <CDropdown
    v-model:show="isDropdownOpen"
    class="w-auto flex flex-col gap-2 relative -top-1"
    :consistent-menu-width="false"
    :on-clickoutside="closeDropdown"
    :options="insertOptions"
    placement="bottom-start"
    :render-option="renderDropdownOption"
    :themeOverrides="themeOverrides"
    trigger="click"
    @select="handleSelect"
  >
    <CButton :themeOverrides="themeOverrides[selectedOverride]" @click="openDropdown">
      <CIcon icon="plus" size="16px" />
      <CIcon icon="chevron-down" size="12px" />
    </CButton>
  </CDropdown>
</template>

<script lang="ts" setup>
import type { Editor } from '@tiptap/vue-3'
import { type StyleValue, h, ref } from 'vue'
import type { DropdownOption } from './CEditorMenu.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { useI18n } from 'vue-i18n'

enum Insert {
  LINK = 'link',
  CONTENT_LINK = 'contentLink',
  TABLE = 'table',
}

interface Props {
  editor: Editor
  themeOverrides: { active: StyleValue; default: StyleValue }
}

const emit = defineEmits<{
  (e: 'on-link-click'): void
  (e: 'on-content-link-click'): void
  (e: 'on-dynamic-value-click'): void
}>()

const props = defineProps<Props>()

const { t } = useI18n()

const insertOptions = [
  {
    key: Insert.LINK,
    icon: 'link',
    label: t('canvasUI.CRichTextEditor.link'),
  },
  {
    key: Insert.CONTENT_LINK,
    icon: 'paperclip',
    label: t('canvasUI.CRichTextEditor.contentLink'),
  },
  {
    key: Insert.TABLE,
    icon: 'table',
    label: t('canvasUI.CRichTextEditor.table'),
  },
]

const renderDropdownOption = ({ option }: { option: DropdownOption }) =>
  h(
    'div',
    {
      class: 'flex items-center uppercase font-500 gap-2 px-2 h-9 cursor-pointer hover:bg-gray-100 tracking-wide',
      onClick: () => {
        handleSelect(option.key)
      },
    },
    [
      h(CIcon, {
        icon: option.icon,
        faType: 'fa',
        size: 16,
      }),
      h('span', { class: 'c-editor-menu__select-label' }, option.label),
    ],
  )

const selectedOverride = ref<'default' | 'active'>('default')
const isDropdownOpen = ref(false)

const openDropdown = () => {
  selectedOverride.value = 'active'
  isDropdownOpen.value = true
}

const closeDropdown = () => {
  selectedOverride.value = 'default'
  isDropdownOpen.value = false
}

const handleSelect = (key: string | number) => {
  let endPosition
  switch (key) {
    case Insert.TABLE:
      props.editor
        .chain()
        .focus()
        .insertContent('<p> </p>')
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run()

      endPosition = props.editor?.state?.doc?.content?.size

      if (endPosition)
        props.editor
          .chain()
          .insertContentAt(endPosition, {
            type: 'paragraph',
            content: [{ type: 'text', text: ' ' }],
          })
          .run()

      break
    case Insert.LINK:
      emit('on-link-click')
      break
    case Insert.CONTENT_LINK:
      emit('on-content-link-click')
      break
    default:
      break
  }
  closeDropdown()
}
</script>
